import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../admin/auth/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function GatePage() {
  const navigate = useNavigate();
  const { state, verifySessionOnce } = useAuth();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const redirecting = useRef(false);

  useEffect(() => {
    // Add noindex meta tag
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex,nofollow';
    document.head.appendChild(meta);
    
    document.title = 'Access Gate - BirdenMedia';

    // Check if already logged in (optional nicety)
    verifySessionOnce();

    return () => {
      if (document.head.contains(meta)) {
        document.head.removeChild(meta);
      }
    };
  }, [verifySessionOnce]);

  const safeNavigate = (path) => {
    if (redirecting.current) return;
    redirecting.current = true;
    navigate(path, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter access code');
      triggerShake();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/auth/gate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ code: code.trim() })
      });

      const data = await response.json();

      if (response.ok) {
        // Success - navigate to login
        safeNavigate('/admin-panel/login');
      } else {
        setError(data.detail || 'Invalid code');
        triggerShake();
        setCode('');
      }
    } catch (err) {
      console.error('Gate error:', err);
      setError('Connection error. Please try again.');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card 
        className={`w-full max-w-md bg-slate-800/50 border-slate-700/50 ${shake ? 'animate-shake' : ''}`}
      >
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <Lock size={32} className="text-blue-400" />
          </div>
          <CardTitle className="text-2xl font-bold text-white">
            Operator Console
          </CardTitle>
          <p className="text-slate-400 text-sm mt-2">
            Enter access code to continue
          </p>
        </CardHeader>
        
        <CardContent>
          {/* Show continue button if already logged in */}
          {state.sessionOk === true && (
            <div className="mb-4 p-3 bg-green-900/20 border border-green-900/50 rounded text-green-400 text-sm text-center">
              Already logged in
              <Button
                onClick={() => safeNavigate('/admin')}
                className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white"
              >
                Continue to Admin
              </Button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="password"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                  setError('');
                }}
                placeholder="Access Code"
                className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 text-center text-lg tracking-wider"
                autoFocus
                disabled={loading}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-900/50 rounded text-red-400 text-sm">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            <Button
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading}
            >
              {loading ? 'Verifying...' : 'Continue'}
            </Button>

            <p className="text-center text-slate-500 text-xs mt-4">
              Need help? Contact the site owner.
            </p>
          </form>
        </CardContent>
      </Card>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
          20%, 40%, 60%, 80% { transform: translateX(10px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
}
